import React, { useEffect, useState } from "react";
import { IonModal, IonContent, IonHeader, IonToolbar, IonButtons, IonButton } from "@ionic/react";
import ContentContainer from "@components/common/ContentContainer";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import { Button_Close } from "@assets/images";
import AIAssistDisclaimer from "./AIAssistDisclaimer";
import AIAssistFlow from "./AIAssistFlow";
import { clearAnimatedMessageIds } from "./AIAssistMessage";
import { aiAssistDisclaimerAcceptedAtom } from "@state/AIAssistState";
import { Loadable, useRecoilStateLoadable } from "recoil";
import { loadableHasValue } from "@utils/recoilUtils";
import { IEvidence } from "@interfaces/Interfaces";
import { AIMessage } from "@interfaces/AIAssistTypes";
import { NavigationBar } from "@hugotomazi/capacitor-navigation-bar";
import { StatusBar } from "@capacitor/status-bar";
import { themeColors } from "@config/tailwindTheme";

type Props = {
  visible: boolean;
  closeModal: () => void;
  onEvidencePreviewSaved: (params: { evidence: IEvidence | null; sessionId: string; messages: AIMessage[] }) => void;
  initialEvidencePreview?: IEvidence | null;
};

type AIAssistModalStatus = "DISCLAIMER" | "PROMPT" | null;

const getStatus = (aiAssistDisclaimerAccepted: Loadable<boolean>): AIAssistModalStatus => {
  if (loadableHasValue(aiAssistDisclaimerAccepted) && aiAssistDisclaimerAccepted.contents) {
    return "PROMPT";
  }

  return "DISCLAIMER";
};

type NavbarColors = "AI_ASSIST_NAVBAR" | "NORMAL_NAVBAR";

const navbarColors: Record<NavbarColors, string> = {
  AI_ASSIST_NAVBAR: themeColors["ai-assist-background"],
  NORMAL_NAVBAR: themeColors.white,
} as const;

type StatusBarColors = "AI_ASSIST_STATUS_BAR" | "NORMAL_STATUS_BAR";

const statusBarColors: Record<StatusBarColors, string> = {
  AI_ASSIST_STATUS_BAR: themeColors["ai-assist-background"],
  NORMAL_STATUS_BAR: themeColors["portfolio-blue"],
} as const;

const handleNavbarChange = async (params: { navbarColor: NavbarColors; statusBarColor: StatusBarColors }) => {
  if (!(await DataController.isWebAsync()) && Capacitor.getPlatform() === "android") {
    try {
      NavigationBar.setColor({ color: navbarColors[params.navbarColor] });
      StatusBar.setBackgroundColor({ color: statusBarColors[params.statusBarColor] });
    } catch (error) {
      console.log(error);
    }
  }
};

const AIAssistModal: React.FC<Props> = (props) => {
  const [aiAssistDisclaimerAccepted, setAiAssistDisclaimerAccepted] =
    useRecoilStateLoadable(aiAssistDisclaimerAcceptedAtom);
  const [status, setStatus] = useState<AIAssistModalStatus>(null);

  const onDismiss = () => {
    props.closeModal();
    setStatus("DISCLAIMER");
    clearAnimatedMessageIds();
  };

  useEffect(() => {
    if (props.visible) {
      handleNavbarChange({ navbarColor: "AI_ASSIST_NAVBAR", statusBarColor: "AI_ASSIST_STATUS_BAR" });
    } else {
      handleNavbarChange({ navbarColor: "NORMAL_NAVBAR", statusBarColor: "NORMAL_STATUS_BAR" });
    }

    return () => {
      handleNavbarChange({ navbarColor: "NORMAL_NAVBAR", statusBarColor: "NORMAL_STATUS_BAR" });
    };
  }, [props.visible]);

  // Clear animated message IDs when modal closes
  useEffect(() => {
    if (!props.visible) {
      clearAnimatedMessageIds();
    }
  }, [props.visible]);

  useEffect(() => {
    if (loadableHasValue(aiAssistDisclaimerAccepted)) {
      setStatus(getStatus(aiAssistDisclaimerAccepted));
    }
  }, [aiAssistDisclaimerAccepted, status, getStatus]);

  const onDisclaimerButtonPressed = () => {
    setAiAssistDisclaimerAccepted(true);
    setStatus("PROMPT");
  };

  const onEvidencePreviewSaved = (params: { evidence: IEvidence | null; sessionId: string; messages: AIMessage[] }) => {
    props.onEvidencePreviewSaved(params);
    onDismiss();
  };

  return (
    <IonModal
      id="ai-assist-modal"
      isOpen={props.visible}
      onDidDismiss={onDismiss}
      canDismiss={true}
      className="fullscreen"
    >
      <IonHeader className="ion-no-border navBarAiAssist">
        <IonToolbar
          mode="ios"
          className="navBar navBarAiAssist"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="end">
            <IonButton
              className="cancelButton"
              mode="ios"
              onClick={() => props.closeModal()}
            >
              <img
                src={Button_Close}
                className="closeIcon"
                alt=""
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background ai-assist-background">
        <ContentContainer>
          {(!status || status === "DISCLAIMER") && (
            <AIAssistDisclaimer disclaimerButtonPressed={onDisclaimerButtonPressed} />
          )}
          {status === "PROMPT" && (
            <AIAssistFlow
              onEvidencePreviewSaved={onEvidencePreviewSaved}
              initialEvidencePreview={props.initialEvidencePreview}
            />
          )}
        </ContentContainer>
      </IonContent>
    </IonModal>
  );
};

export default AIAssistModal;
